import {
  IconButton,
  MenuItem,
  MenuSeparator,
  toast,
  useConfirmModal,
} from '@affine/component';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import { Guard } from '@affine/core/components/guard';
import { useBlockSuiteMetaHelper } from '@affine/core/components/hooks/affine/use-block-suite-meta-helper';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
// import { IsFavoriteIcon } from '@affine/core/components/pure/icons';
import { DocsService } from '@affine/core/modules/doc';
import { CompatibleFavoriteItemsAdapter } from '@affine/core/modules/favorite';
import {
  type FolderNode,
  OrganizeService,
  DocTitleUniquenessService,
} from '@affine/core/modules/organize';
import { GuardService } from '@affine/core/modules/permissions';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { truncateTitleForModal } from '@affine/core/utils';
import { track } from '@affine/track';
import {
  DeleteIcon,
  DuplicateIcon,
  InformationIcon,
  LinkedPageIcon,
  OpenInNewIcon,
  PlusIcon,
  SplitViewIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import type { NodeOperation } from '../../tree/types';

export const useNavigationPanelDocNodeOperations = (
  docId: string,
  options: {
    openInfoModal: () => void;
    openNodeCollapsed: () => void;
  },
  node: FolderNode
): NodeOperation[] => {
  const t = useI18n();
  const {
    workbenchService,
    workspaceService,
    docsService,
    compatibleFavoriteItemsAdapter,
    guardService,
    organizeService,
    docTitleUniquenessService,
  } = useServices({
    DocsService,
    WorkbenchService,
    WorkspaceService,
    CompatibleFavoriteItemsAdapter,
    GuardService,
    OrganizeService,
    DocTitleUniquenessService,
  });
  const { openConfirmModal } = useConfirmModal();

  const [addLinkedPageLoading, setAddLinkedPageLoading] = useState(false);
  const docRecord = useLiveData(docsService.list.doc$(docId));

  const { createPage } = usePageHelper(
    workspaceService.workspace.docCollection
  );

  const favorite = useLiveData(
    useMemo(() => {
      return compatibleFavoriteItemsAdapter.isFavorite$(docId, 'doc');
    }, [docId, compatibleFavoriteItemsAdapter])
  );

  const { duplicate } = useBlockSuiteMetaHelper();
  const handleDuplicate = useAsyncCallback(async () => {
    const newPageId = await duplicate(docId, true);

    // 获取当前节点的父节点信息
    const nodeInfo = node.info$.value;
    const parentId = nodeInfo?.parentId;

    // 在父节点上添加链接
    if (parentId) {
      // 获取父节点
      const parentNode = organizeService.folderTree.folderNode$(parentId).value;
      if (parentNode) {
        // 获取父节点对应的文档ID
        const parentNodeInfo = parentNode.info$.value;
        const parentDocId = parentNodeInfo?.data;

        if (parentDocId && parentNodeInfo?.type === 'doc') {
          // 在父文档上添加链接
          await docsService.addLinkedDoc(parentDocId, newPageId);
        }

        try {
          await docTitleUniquenessService.ensureUniqueTitleInFolder(
            newPageId,
            parentNode
          );
          parentNode.createLink('doc', newPageId, parentNode.indexAt('before'));
        } catch (error) {
          console.warn('Failed to add document to parent organize:', error);
        }
      }
    } else {
      // 如果没有父节点，则在根节点下创建
      try {
        const rootNode = organizeService.folderTree.rootFolder;
        await docTitleUniquenessService.ensureUniqueTitleInFolder(
          newPageId,
          rootNode
        );
        rootNode.createLink('doc', newPageId, rootNode.indexAt('before'));
      } catch (error) {
        console.warn('Failed to add document to root organize:', error);
      }
    }

    // 追踪organize相关的事件
    // track.$.navigationPanel.organize.createOrganizeItem({
    //   type: 'link',
    //   target: 'doc',
    // });

    // track.$.navigationPanel.docs.createDoc();\
    track.$.navigationPanel.docs.asset_item_click({
      click_event: 'duplicate',
    });
  }, [
    docId,
    duplicate,
    docsService,
    node,
    organizeService,
    docTitleUniquenessService,
  ]);
  // const handleOpenInfoModal = useCallback(() => {
  //   track.$.docInfoPanel.$.open();
  //   options.openInfoModal();
  // }, [options]);

  const handleMoveToTrash = useCallback(() => {
    if (!docRecord) {
      return;
    }
    openConfirmModal({
      title: t['com.affine.moveToTrash.title'](),
      description: t['com.affine.moveToTrash.confirmModal.description']({
        title: truncateTitleForModal(docRecord.title$.value || t['Untitled']()),
      }),
      confirmText: t['com.affine.moveToTrash.confirmModal.confirm'](),
      cancelText: t['com.affine.moveToTrash.confirmModal.cancel'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm() {
        // 递归删除所有子节点
        const deleteNodeAndChildren = (currentNode: FolderNode) => {
          // 获取当前节点的所有子节点
          const children = currentNode.sortedChildren$.value;

          // 递归删除所有子节点
          children.forEach(child => {
            const childInfo = child.info$.value;
            if (childInfo?.type === 'doc') {
              // 如果是文档节点，获取对应的文档记录并移动到垃圾桶
              const childDocRecord = docsService.list.doc$(
                childInfo.data
              ).value;
              if (childDocRecord) {
                childDocRecord.moveToTrash();
                if (child.id) {
                  child.delete();
                }
              }
            }
            // 删除organize链接
            // child.delete();
            // 递归处理子节点的子节点
            deleteNodeAndChildren(child);
          });
        };

        // 删除当前节点及其所有子节点
        deleteNodeAndChildren(node);

        // 移动当前文档到垃圾桶
        docRecord.moveToTrash();

        // 删除当前节点的organize链接
        if (node.id) {
          node.delete();
        }

        // track.$.navigationPanel.docs.deleteDoc({
        //   control: 'button',
        // });
        track.$.navigationPanel.docs.asset_item_click({
          click_event: 'move_to_trash',
        });
        toast(t['com.affine.toastMessage.movedTrash']());
      },
    });
  }, [docRecord, openConfirmModal, t, node, docsService]);

  const handleOpenInNewTab = useCallback(() => {
    workbenchService.workbench.openDoc(`${docId}?refer=other`, {
      at: 'new-tab',
    });
    track.$.navigationPanel.docs.asset_item_click({
      click_event: 'open_in_new_tab',
    });
    // track.$.navigationPanel.organize.openInNewTab({
    //   type: 'doc',
    // });
  }, [docId, workbenchService]);

  const handleOpenInSplitView = useCallback(() => {
    workbenchService.workbench.openDoc(`${docId}?refer=other`, {
      at: 'beside',
    });
    track.$.navigationPanel.docs.openDoc();
    track.$.navigationPanel.organize.openInSplitView({
      type: 'doc',
    });
  }, [docId, workbenchService.workbench]);

  const handleAddLinkedPage = useAsyncCallback(async () => {
    setAddLinkedPageLoading(true);
    try {
      const canEdit = await guardService.can('Doc_Update', docId);
      if (!canEdit) {
        toast(t['com.affine.no-permission']());
        return;
      }
      const newDoc = createPage();
      // TODO: handle timeout & error
      await docsService.addLinkedDoc(docId, newDoc.id);

      // 新增：在organize中创建link
      try {
        node.createLink('doc', newDoc.id, node.indexAt('before'));
        await docTitleUniquenessService.ensureUniqueTitleInFolder(
          newDoc.id,
          node
        );

        // 追踪organize相关的事件
        track.$.navigationPanel.organize.createOrganizeItem({
          type: 'link',
          target: 'doc',
        });
      } catch (error) {
        console.warn('Failed to add document to organize:', error);
        // 不要让organize的错误阻止文档创建流程
      }
      track.$.navigationPanel.docs.asset_item_click({
        click_event: 'create_new_doc',
        parent_doc_id: docId,
      });
      // track.$.navigationPanel.docs.createDoc({ control: 'linkDoc' });
      // track.$.navigationPanel.docs.linkDoc({ control: 'createDoc' });
      options.openNodeCollapsed();
    } finally {
      setAddLinkedPageLoading(false);
    }
  }, [
    createPage,
    guardService,
    docId,
    docsService,
    options,
    t,
    node,
    docTitleUniquenessService,
  ]);

  const handleToggleFavoriteDoc = useCallback(() => {
    compatibleFavoriteItemsAdapter.toggle(docId, 'doc');
    track.$.navigationPanel.organize.toggleFavorite({
      type: 'doc',
    });
  }, [docId, compatibleFavoriteItemsAdapter]);

  return useMemo(
    () => [
      {
        index: 0,
        inline: true,
        view: (
          <IconButton
            size="16"
            icon={<PlusIcon />}
            tooltip={t['com.affine.rootAppSidebar.explorer.doc-add-tooltip']()}
            onClick={handleAddLinkedPage}
            loading={addLinkedPageLoading}
            disabled={addLinkedPageLoading}
          />
        ),
      },
      // {
      //   index: 50,
      //   view: (
      //     <MenuItem
      //       prefixIcon={<InformationIcon />}
      //       onClick={handleOpenInfoModal}
      //     >
      //       {t['com.affine.page-properties.page-info.view']()}
      //     </MenuItem>
      //   ),
      // },
      // {
      //   index: 99,
      //   view: (
      //     <Guard docId={docId} permission="Doc_Update">
      //       {canEdit => (
      //         <MenuItem
      //           prefixIcon={<LinkedPageIcon />}
      //           onClick={handleAddLinkedPage}
      //           disabled={!canEdit}
      //         >
      //           {t['com.affine.page-operation.add-linked-page']()}
      //         </MenuItem>
      //       )}
      //     </Guard>
      //   ),
      // },
      {
        index: 99,
        view: (
          <MenuItem prefixIcon={<DuplicateIcon />} onClick={handleDuplicate}>
            {t['com.affine.header.option.duplicate']()}
          </MenuItem>
        ),
      },
      {
        index: 99,
        view: (
          <MenuItem prefixIcon={<OpenInNewIcon />} onClick={handleOpenInNewTab}>
            {t['com.affine.workbench.tab.page-menu-open']()}
          </MenuItem>
        ),
      },
      ...(BUILD_CONFIG.isElectron
        ? [
            {
              index: 100,
              view: (
                <MenuItem
                  prefixIcon={<SplitViewIcon />}
                  onClick={handleOpenInSplitView}
                >
                  {t['com.affine.workbench.split-view.page-menu-open']()}
                </MenuItem>
              ),
            },
          ]
        : []),
      //  yuanshi-20250807-先注释添加favourites
      // {
      //   index: 199,
      //   view: (
      //     <MenuItem
      //       prefixIcon={<IsFavoriteIcon favorite={favorite} />}
      //       onClick={handleToggleFavoriteDoc}
      //     >
      //       {favorite
      //         ? t['com.affine.favoritePageOperation.remove']()
      //         : t['com.affine.favoritePageOperation.add']()}
      //     </MenuItem>
      //   ),
      // },
      {
        index: 9999,
        view: <MenuSeparator key="menu-separator" />,
      },
      {
        index: 10000,
        view: (
          <Guard docId={docId} permission="Doc_Trash">
            {canMoveToTrash => (
              <MenuItem
                type={'danger'}
                prefixIcon={<DeleteIcon />}
                onClick={handleMoveToTrash}
                disabled={!canMoveToTrash}
              >
                {t['com.affine.moveToTrash.title']()}
              </MenuItem>
            )}
          </Guard>
        ),
      },
    ],
    [
      addLinkedPageLoading,
      docId,
      // favorite,
      handleAddLinkedPage,
      handleDuplicate,
      handleMoveToTrash,
      handleOpenInNewTab,
      handleOpenInSplitView,
      // handleOpenInfoModal,
      // handleToggleFavoriteDoc,
      t,
    ]
  );
};
